import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/apiHelpers';
import { Search, RefreshCw, Copy, Users, User, CheckCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * UUID Lookup Tool Component
 * 
 * Bidirectional lookup tool for super admins:
 * 1. Name/MRN -> UUID: Search by name or MRN to find UUID
 * 2. UUID -> Name/MRN: Paste a UUID to find the person's details
 * 
 * @param {boolean} open - Controls dialog visibility
 * @param {function} onOpenChange - Callback when dialog open state changes
 */
export const UUIDLookup = ({ open, onOpenChange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('both');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    
    if (searchTerm.trim().length < 2) {
      toast.error('Search term must be at least 2 characters');
      return;
    }
    
    setLoading(true);
    setError(null);
    setResults(null);
    
    try {
      const response = await axios.post(`${API}/admin/tools/lookup-uuid`, {
        search_term: searchTerm.trim(),
        search_type: searchType
      }, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem('token')}`
        }
      });
      
      setResults(response.data);
      
      if (response.data.total_found > 0) {
        toast.success(`Found ${response.data.total_found} matching record(s)`);
      } else {
        toast.info('No matching records found');
      }
    } catch (error) {
      console.error('UUID lookup failed:', error);
      setError(getApiErrorMessage(error, 'UUID lookup failed'));
      toast.error('UUID lookup failed');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setSearchTerm('');
    setSearchType('both');
    setResults(null);
    setError(null);
  };

  const handleCopyUUID = (uuid) => {
    navigator.clipboard.writeText(uuid);
    toast.success('UUID copied to clipboard');
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-purple-900">
            <Search className="h-5 w-5" />
            UUID Lookup Tool
          </DialogTitle>
          <DialogDescription>
            Bidirectional lookup: Search by name/MRN to find UUID, or paste a UUID to find the person.
          </DialogDescription>
        </DialogHeader>
        
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
          <p className="text-sm text-purple-800 font-medium">Bidirectional Lookup</p>
          <p className="text-xs text-purple-700 mt-1">
            <strong>Name → UUID:</strong> Enter a name, email, or MRN to find the associated UUID(s).<br/>
            <strong>UUID → Name:</strong> Paste a UUID to find the client name, MRN, and details.
          </p>
        </div>
        
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="flex-1">
              <Label>Search Term</Label>
              <Input
                placeholder="Enter name, email, MRN, or UUID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                className="mt-1"
              />
            </div>
            <div className="w-40">
              <Label>Search In</Label>
              <Select value={searchType} onValueChange={setSearchType}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="both">Both</SelectItem>
                  <SelectItem value="client">Clients Only</SelectItem>
                  <SelectItem value="user">Users Only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <Button 
            onClick={handleSearch} 
            disabled={loading || !searchTerm.trim()}
            className="w-full bg-purple-600 hover:bg-purple-700"
          >
            {loading ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Searching...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Search for UUID
              </>
            )}
          </Button>
          
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800 font-medium">Error</p>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
          
          {results && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className={results.total_found > 0 ? "text-green-800" : "text-gray-800"}>
                  Search Results
                </Label>
                <Badge className={results.total_found > 0 ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"}>
                  {results.total_found} result(s) found
                </Badge>
              </div>
              
              {/* Clients Results */}
              {results.results.clients && results.results.clients.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <Users className="h-4 w-4" />
                    Clients ({results.results.clients.length})
                  </div>
                  <div className="space-y-2">
                    {results.results.clients.map((client, idx) => (
                      <div 
                        key={idx} 
                        className="bg-blue-50 border border-blue-200 rounded-lg p-3"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-blue-900">{client.name}</p>
                            {client.mrn && (
                              <p className="text-sm text-blue-800 font-mono">
                                {client.mrn}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="ml-1 h-5 px-1"
                                  onClick={() => handleCopyUUID(client.mrn)}
                                  title="Copy MRN"
                                >
                                  <Copy className="h-3 w-3" />
                                </Button>
                              </p>
                            )}
                            <p className="text-xs text-blue-700">{client.email}</p>
                            {client.dob && (
                              <p className="text-xs text-blue-600">DOB: {client.dob}</p>
                            )}
                            {client.phone && (
                              <p className="text-xs text-blue-600">Phone: {client.phone}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {client.status || 'active'}
                              </Badge>
                              {client.lookup_type === 'uuid_to_name' && (
                                <Badge className="text-xs bg-purple-100 text-purple-800">
                                  UUID Match
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right ml-3">
                            <p className="text-xs text-gray-500 mb-1">UUID:</p>
                            <code className="text-xs bg-white px-2 py-1 rounded border block mb-1">{client.id}</code>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2"
                              onClick={() => handleCopyUUID(client.id)}
                              title="Copy UUID"
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              Copy
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Users Results */}
              {results.results.users && results.results.users.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                    <User className="h-4 w-4" />
                    Users/Providers ({results.results.users.length})
                  </div>
                  <div className="space-y-2">
                    {results.results.users.map((user, idx) => (
                      <div 
                        key={idx} 
                        className="bg-green-50 border border-green-200 rounded-lg p-3"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-green-900">{user.name}</p>
                            <p className="text-xs text-green-700">{user.email}</p>
                            {user.npi && (
                              <p className="text-xs text-green-600 font-mono">NPI: {user.npi}</p>
                            )}
                            <div className="flex items-center gap-2 mt-1">
                              <Badge variant="outline" className="text-xs">
                                {user.role || 'user'}
                              </Badge>
                              {!user.is_active && (
                                <Badge variant="destructive" className="text-xs">
                                  Inactive
                                </Badge>
                              )}
                              {user.lookup_type === 'uuid_to_name' && (
                                <Badge className="text-xs bg-purple-100 text-purple-800">
                                  UUID Match
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="text-right ml-3">
                            <p className="text-xs text-gray-500 mb-1">UUID:</p>
                            <code className="text-xs bg-white px-2 py-1 rounded border block mb-1">{user.id}</code>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2"
                              onClick={() => handleCopyUUID(user.id)}
                              title="Copy UUID"
                            >
                              <Copy className="h-3 w-3 mr-1" />
                              Copy
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* No Results */}
              {results.total_found === 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <p className="text-gray-600">No matching records found for "{results.search_term}"</p>
                  <p className="text-xs text-gray-500 mt-1">Try a different search term or search type</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default UUIDLookup;
